<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  formatHotkey,
  getHotkeyFromEvent,
  HOTKEY_ACTIONS,
  isValidHotkey,
  type HotkeyAction,
  type HotkeyMap,
} from '@/composables/useHotkeys'

const props = defineProps<{
  visible: boolean
  hotkeys: HotkeyMap
  findConflict: (action: HotkeyAction, keys: string[]) => HotkeyAction | undefined
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'reset'): void
  (e: 'set-hotkey', action: HotkeyAction, keys: string[]): void
}>()

const { t } = useI18n()

const conflictAction = ref<HotkeyAction | null>(null)
const conflictInputs = ref<Partial<Record<HotkeyAction, string[]>>>({})

const hotkeyLabels: Record<HotkeyAction, string> = {
  powerToggle: t('hotkey.powerToggle', 'Power Toggle'),
  eqToggle: t('hotkey.eqToggle', 'EQ Settings'),
  rgbToggle: t('hotkey.rgbToggle', 'RGB Toggle'),
  aiToggle: t('hotkey.aiToggle', 'AI ENC'),
  balenceToggle: t('hotkey.balenceToggle', 'Voice Balance'),
  Positioning: t('hotkey.Positioning', 'Positioning'),
}

const title = computed(() => t('hotkey.dialogTitle', 'Hotkey Settings'))
const notSetText = computed(() => t('hotkey.notSet', 'Not Set'))

watch(
  () => props.visible,
  visible => {
    if (!visible) {
      conflictAction.value = null
      conflictInputs.value = {}
    }
  },
)

const getDisplayValue = (action: HotkeyAction) => {
  return formatHotkey(conflictInputs.value[action] || props.hotkeys[action], notSetText.value)
}

const getConflictTitle = (action: HotkeyAction) => {
  const conflict = conflictAction.value
  if (!conflict || !conflictInputs.value[action]) return ''
  return `${t('hotkey.conflict', 'Hotkey conflict: already used by')} ${hotkeyLabels[conflict]} ${t('hotkey.conflictUsed', 'Used')}`
}

const onKeydown = (action: HotkeyAction, event: KeyboardEvent) => {
  event.preventDefault()
  event.stopPropagation()

  const keys = getHotkeyFromEvent(event)
  if (!isValidHotkey(keys)) return

  const conflict = props.findConflict(action, keys)
  if (conflict) {
    conflictAction.value = conflict
    conflictInputs.value = { ...conflictInputs.value, [action]: keys }
    return
  }

  conflictAction.value = null
  conflictInputs.value = { ...conflictInputs.value, [action]: undefined }
  emit('set-hotkey', action, keys)
}

const reset = () => {
  conflictAction.value = null
  conflictInputs.value = {}
  emit('reset')
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="hotkey-overlay" @click.self="emit('close')">
      <section class="hotkey-dialog" role="dialog" aria-modal="true" :aria-label="title">
        <header class="hotkey-header">
          <h2>{{ title }}</h2>
          <button class="close-btn" type="button" @click="emit('close')">
            <el-icon><Close /></el-icon>
          </button>
        </header>

        <div class="hotkey-list">
          <div v-for="action in HOTKEY_ACTIONS" :key="action" class="hotkey-item">
            <span class="hotkey-label">{{ hotkeyLabels[action] }}</span>
            <div class="hotkey-input-wrap">
              <input
                readonly
                :class="{ conflict: !!conflictInputs[action] }"
                :value="getDisplayValue(action)"
                @keydown="onKeydown(action, $event)"
              >
              <span v-if="conflictInputs[action]" class="hotkey-conflict-tip" :title="getConflictTitle(action)">
                {{ t('hotkey.occupied', 'Hotkey is occupied') }}
              </span>
            </div>
          </div>
        </div>

        <footer class="hotkey-footer">
          <span>{{ t('hotkey.tip', 'Click input box and press keys') }}</span>
          <button type="button" class="reset-btn" @click="reset">
            {{ t('hotkey.reset', 'Reset') }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.hotkey-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.52);
}

.hotkey-dialog {
  width: 550px;
  max-width: calc(100vw - 32px);
  padding: 0;
  color: rgba(255, 255, 255, 0.78);
  background: #18191f;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
}

.hotkey-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }
}

.close-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.62);
  background: transparent;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.92);
    background: rgba(255, 255, 255, 0.08);
  }
}

.hotkey-list {
  margin: 24px 30px 0;
  padding: 22px 30px;
  background: rgba(25, 25, 26, 0.8);
  border-radius: 8px;
}

.hotkey-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 44px;
}

.hotkey-label {
  min-width: 130px;
  font-size: 14px;
}

.hotkey-input-wrap {
  position: relative;
  width: 180px;
  flex: 0 0 180px;
}

.hotkey-input-wrap input {
  width: 100%;
  height: 32px;
  padding: 0 12px;
  color: rgba(255, 255, 255, 0.72);
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  outline: none;

  &:focus {
    border-color: #376bed;
  }

  &.conflict {
    color: #ff4d4f;
    border-color: #ff4d4f;
  }
}

.hotkey-conflict-tip {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  color: #ff4d4f;
  font-size: 12px;
  white-space: nowrap;
}

.hotkey-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 30px 24px;
  color: #888;
  font-size: 12px;
}

.reset-btn {
  height: 34px;
  padding: 0 16px;
  color: rgba(255, 255, 255, 0.76);
  background: #333;
  border: 0;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #3d3d3d;
  }
}
</style>
